import type { SearchEntry, SearchResult } from "./search.types";

// Score weights, highest first: a label prefix is the strongest signal, a label
// substring next, then keyword and description hits. 0 means no match.
const SCORE = {
	labelPrefix: 100,
	labelIncludes: 60,
	keyword: 40,
	descriptionIncludes: 20,
} as const;

function scoreEntry(entry: SearchEntry, query: string): number {
	const label = entry.label.toLowerCase();
	if (label.startsWith(query)) return SCORE.labelPrefix;
	if (label.includes(query)) return SCORE.labelIncludes;
	if (entry.keywords.some((k) => k.toLowerCase().includes(query))) {
		return SCORE.keyword;
	}
	if (entry.description.toLowerCase().includes(query)) {
		return SCORE.descriptionIncludes;
	}
	return 0;
}

// Pure ranking: filter `entries` to those matching `query`, ordered best-first.
// An empty query returns every entry in its original (index) order.
export function rankEntries<TEntry extends SearchEntry>(
	entries: readonly TEntry[],
	query: string,
): SearchResult<TEntry>[] {
	const q = query.trim().toLowerCase();
	if (q === "") return entries.map((entry) => ({ entry, score: 0 }));

	return entries
		.map((entry) => ({ entry, score: scoreEntry(entry, q) }))
		.filter((r) => r.score > 0)
		.sort((a, b) => b.score - a.score);
}
